import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Landmark } from 'lucide-react';
import { SUBSCRIPTION_TIER_LABELS } from '@reflex/shared';
import { Badge } from '@reflex/ui/components/ui/badge';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SearchInput } from '@reflex/ui/components/shared/SearchInput';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { CopyableId } from '@reflex/ui/components/CopyableId';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { formatDateOnly } from '@reflex/ui/lib/format';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { ROUTES } from '@/lib/routes';

type AdminCompany = Awaited<
  ReturnType<typeof api.listAdminBillingEntities>
>['billingEntities'][number];

const companyColumns: DataTableColumn<AdminCompany>[] = [
  {
    header: 'Name',
    cell: (company) => (
      <Link
        to={ROUTES.admin.company(company.id)}
        data-testid={`admin-company-row-${company.id}`}
        aria-label={`View company ${company.name}`}
        className="block min-w-0 transition-opacity hover:opacity-80"
      >
        <p className="truncate font-medium">{company.name}</p>
        <p className="truncate text-xs text-muted-foreground">{company.slug}</p>
      </Link>
    ),
  },
  {
    header: 'ID',
    cell: (company) => <CopyableId id={company.id} label={`Copy company id ${company.id}`} />,
    cellClassName: 'whitespace-nowrap',
  },
  {
    header: 'Plan',
    cell: (company) => (
      <Badge variant="secondary">{SUBSCRIPTION_TIER_LABELS[company.subscriptionTier]}</Badge>
    ),
  },
  {
    header: 'Created',
    cell: (company) => formatDateOnly(company.createdAt),
    cellClassName: 'text-xs text-muted-foreground',
  },
];

/** Case-insensitive match on the fields an operator is likely to paste. */
function matchesSearch(company: AdminCompany, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [company.name, company.slug, company.id].some((field) =>
    field.toLowerCase().includes(needle),
  );
}

/**
 * Platform-admin list of companies (billing entities): the commercial owners
 * of organizations. Each row opens the company page, where support changes
 * its plan and manages its email-domain claims. The server returns every
 * company in one response, so search filters client-side.
 */
export function AdminCompaniesPage() {
  const [q, setQ] = useState('');
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.adminBillingEntities,
    queryFn: () => api.listAdminBillingEntities(),
  });

  const companies = useMemo(() => data?.billingEntities ?? [], [data]);
  const visible = useMemo(() => companies.filter((c) => matchesSearch(c, q)), [companies, q]);
  const hasSearch = q.trim().length > 0;

  return (
    <AdminSubPageLayout
      title="Companies"
      description="Every company on this instance. A company owns one or more organizations and holds their plan and email domains."
      icon={<Landmark />}
    >
      <SectionCard
        title="All Companies"
        description={
          isLoading
            ? 'Loading…'
            : `${companies.length.toLocaleString()} ${companies.length === 1 ? 'company' : 'companies'}`
        }
        icon={<Landmark />}
        contentClassName="space-y-3 pt-0"
      >
        <SearchInput
          value={q}
          onValueChange={setQ}
          placeholder="Search by name, slug, or ID…"
          aria-label="Search companies"
          data-testid="admin-companies-search"
        />
        <AsyncContent
          isLoading={isLoading}
          isError={isError}
          errorMessage="Failed to load companies."
        >
          {visible.length === 0 ? (
            <EmptyState
              variant="bordered"
              icon={<Landmark className="h-8 w-8" />}
              title={hasSearch ? 'No companies match your search' : 'No companies yet'}
              description={
                hasSearch
                  ? 'Try a different name, slug, or ID.'
                  : 'Creating an organization creates its company.'
              }
            />
          ) : (
            <DataTable<AdminCompany>
              data={visible}
              columns={companyColumns}
              rowKey={(company) => company.id}
            />
          )}
        </AsyncContent>
      </SectionCard>
    </AdminSubPageLayout>
  );
}
